import { redirectToLogin, refreshSession } from './auth';
import { authHeaders } from './entitlements';

export interface PrescriberView {
  nombre: string;
  registroAcess: string;
  telefono: string;
  hasFirma: boolean;
}

export interface EncounterView {
  id: number;
  seqReceta: string;
  seqHistoria: string;
  dictadoRaw: string;
  receta: unknown;
  historia: unknown;
  createdAt: string;
  createdBy: string;
}

export interface Cie10SearchResult {
  code: string;
  description: string;
  score: number;
  specialty: string;
}

export interface Cie11SearchResult {
  code: string;
  description: string;
  score: number;
  specialty?: string;
  query_specialty?: string;
  uri?: string;
  release?: string;
}

export interface Cie10SearchView {
  available: boolean;
  results: Cie10SearchResult[];
  readiness?: { cie10_training_only?: boolean; cie11_training_only?: boolean };
}

export interface Cie11SearchView {
  available: boolean;
  results: Cie11SearchResult[];
  readiness?: { cie11_training_only?: boolean };
}

export interface NlpFeedbackWrite {
  text: string;
  predicted: Record<string, unknown>;
  corrected: Record<string, unknown>;
  consentTraining: boolean;
  specialty: string;
  diagnosisPhrase?: string;
  cie10Code?: string;
  cie11Code?: string;
}

export interface SpecialtyClassificationView {
  suggestion: string | null;
  score: number;
  margin?: number;
  abstained: boolean;
  reason: string;
  candidates: Array<{ specialty: string; score: number; matched_terms: string[] }>;
  model_version?: string;
}

async function clinicJson<T>(url: string, init?: RequestInit): Promise<T> {
  const request = () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) };
    return fetch(url, { ...init, headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) } });
  };
  let res = await request();
  if (res.status === 401) {
    if (await refreshSession()) res = await request();
    if (res.status === 401) redirectToLogin();
  }
  const text = await res.text();
  if (!res.ok) {
    try {
      const body = JSON.parse(text) as { message?: string };
      throw new Error(body.message || text || res.statusText);
    } catch (err) {
      if (err instanceof SyntaxError) throw new Error(text || res.statusText);
      throw err;
    }
  }
  return (text ? JSON.parse(text) : null) as T;
}

export function loadPrescriber(): Promise<PrescriberView> {
  return clinicJson('/api/clinic/prescriber');
}

export function savePrescriber(body: {
  nombre: string;
  registroAcess: string;
  telefono: string;
  firmaPngBase64?: string;
}): Promise<PrescriberView> {
  return clinicJson('/api/clinic/prescriber', { method: 'PUT', body: JSON.stringify(body) });
}

export function listEncounters(): Promise<{ items: EncounterView[] }> {
  return clinicJson('/api/clinic/encounters');
}

export function getEncounter(id: string): Promise<EncounterView> {
  return clinicJson(`/api/clinic/encounters/${encodeURIComponent(id)}`);
}

export function createEncounter(body: unknown): Promise<EncounterView> {
  return clinicJson('/api/clinic/encounters', { method: 'POST', body: JSON.stringify(body) });
}

export function searchCie10(query: string, specialty: string): Promise<Cie10SearchView> {
  const params = new URLSearchParams({ q: query, specialty, limit: '6' });
  return clinicJson(`/api/nlp/cie10?${params.toString()}`);
}

export function searchCie11(query: string, specialty: string): Promise<Cie11SearchView> {
  const params = new URLSearchParams({ q: query, specialty, limit: '6' });
  return clinicJson(`/api/nlp/cie11?${params.toString()}`);
}

export function classifySpecialty(text: string, specialties: string[]): Promise<SpecialtyClassificationView> {
  return clinicJson('/api/nlp/classify-specialty', {
    method: 'POST', body: JSON.stringify({ text, specialties }),
  });
}

export function submitNlpFeedback(body: NlpFeedbackWrite): Promise<{ id: string; status: string }> {
  return clinicJson('/api/nlp/feedback', { method: 'POST', body: JSON.stringify(body) });
}
